import React, { useState } from 'react';
import { MapPin, Filter } from 'lucide-react';
import { artsImages } from '../mockData';
import '../styles/projectsoftpower.css';

const Arts = () => {
  const [selectedRegion, setSelectedRegion] = useState('All');
  const regions = ['All', 'Europe', 'Asia', 'Americas', 'Africa', 'Middle East', 'Global'];

  const filteredArts = selectedRegion === 'All'
    ? artsImages
    : artsImages.filter(art => art.region === selectedRegion);

  return (
    <div className="arts-page">
      {/* Hero Section */}
      <section className="section-spacing-large" style={{
        background: 'linear-gradient(135deg, var(--color-gray-50) 0%, var(--color-white) 100%)',
        textAlign: 'center'
      }}>
        <div className="container-psp">
          <h1 className="hero-title">Global Arts Collection</h1>
          <p className="hero-tagline" style={{ maxWidth: '800px', margin: '1.5rem auto 0' }}>
            Discover masterpieces from across the globe. From classical paintings to contemporary 
            installations, explore humanity's creative heritage.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section style={{ background: 'var(--color-white)', padding: 'var(--spacing-xl) 0' }}>
        <div className="container-psp">
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-md)',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-gray-700)' }}>
              <Filter size={20} />
              <span className="caption-text" style={{ fontWeight: '600', textTransform: 'uppercase' }}>Filter by Region:</span>
            </div>
            {regions.map((region) => (
              <button
                key={region}
                onClick={() => setSelectedRegion(region)}
                style={{
                  padding: '0.5rem 1.5rem',
                  border: selectedRegion === region
                    ? '2px solid var(--color-indigo)'
                    : '2px solid var(--color-gray-300)',
                  borderRadius: '2rem',
                  background: selectedRegion === region
                    ? 'var(--color-indigo)'
                    : 'transparent',
                  color: selectedRegion === region
                    ? 'var(--color-white)'
                    : 'var(--color-gray-700)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  transition: 'all var(--transition-base)'
                }}
              >
                {region}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Arts Gallery */}
      <section className="section-spacing-large" style={{ background: 'var(--color-gray-50)' }}>
        <div className="container-psp">
          <div className="grid-3">
            {filteredArts.map((art, index) => (
              <div
                key={index}
                className="card-psp image-overlay"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <img
                  src={art.url}
                  alt={art.title}
                  className="card-image"
                  style={{ height: '350px' }}
                />
                <div className="overlay-content">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <MapPin size={16} />
                    <span className="caption-text" style={{ color: 'var(--color-white)' }}>{art.region}</span>
                  </div>
                  <h3 className="card-title" style={{ color: 'var(--color-white)', marginBottom: '0.5rem' }}>
                    {art.title}
                  </h3>
                  <p className="caption-text" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    {art.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {filteredArts.length === 0 && (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
              <p className="body-text">No artworks found for the selected region.</p>
            </div>
          )}
        </div>
      </section>

      {/* Virtual Exhibitions */}
      <section className="section-spacing-large" style={{ background: 'var(--color-white)' }}>
        <div className="container-psp">
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-2xl)' }}>
            <h2 className="section-title">Virtual Exhibitions</h2>
            <p className="body-text" style={{ marginTop: '1rem' }}>
              Immerse yourself in curated collections from world-class museums and galleries
            </p>
          </div>

          <div className="grid-2">
            {[
              {
                title: 'Renaissance Masters',
                description: 'Explore the golden age of European art with works from da Vinci, Michelangelo, and Raphael',
                count: '120 artworks'
              },
              {
                title: 'Contemporary Global Voices',
                description: 'Discover cutting-edge contemporary art from emerging artists across six continents',
                count: '85 artworks'
              },
              {
                title: 'Indigenous Art & Craft',
                description: 'Traditional arts and crafts showcasing indigenous cultural heritage and techniques',
                count: '200+ pieces'
              },
              {
                title: 'Asian Calligraphy',
                description: 'The art of written expression from Chinese, Japanese, Korean, and Arabic traditions',
                count: '150 works'
              }
            ].map((exhibition, index) => (
              <div
                key={index}
                className="accent-border"
                style={{
                  padding: 'var(--spacing-lg)',
                  background: 'var(--color-gray-50)',
                  borderRadius: '0.5rem'
                }}
              >
                <h3 className="subsection-title" style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>
                  {exhibition.title}
                </h3>
                <p className="body-text" style={{ marginBottom: '1rem' }}>
                  {exhibition.description}
                </p>
                <span className="caption-text" style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.75rem',
                  background: 'var(--color-indigo)',
                  color: 'var(--color-white)',
                  borderRadius: '1rem',
                  fontSize: '0.8125rem',
                  fontWeight: '600'
                }}>
                  {exhibition.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-spacing-large" style={{
        background: 'linear-gradient(135deg, var(--color-emerald) 0%, var(--color-indigo) 100%)',
        color: 'var(--color-white)',
        textAlign: 'center'
      }}>
        <div className="container-psp">
          <h2 className="section-title" style={{ color: 'var(--color-white)' }}>
            Submit Your Artwork
          </h2>
          <p className="body-text" style={{
            color: 'rgba(255, 255, 255, 0.95)',
            maxWidth: '600px',
            margin: '1.5rem auto 2rem'
          }}>
            Are you an artist or cultural institution? Share your work with our global community.
          </p>
          <button className="btn-primary" style={{
            background: 'var(--color-white)',
            color: 'var(--color-emerald)',
            border: '2px solid var(--color-white)'
          }}>
            Submit Artwork
          </button>
        </div>
      </section>
    </div>
  );
};

export default Arts;
